import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { formatMoney } from '../../lib/utils'
import type { CaixaDoMes, PlanoDoMes } from '../../types'

const r2 = (v: number) => Math.round(v * 100) / 100

interface Linha { sinal: '+' | '−' | '='; titulo: string; valor: number; deOnde: string }

/** Passo a passo do Plano e do Caixa com os valores do mês, e por que os dois são diferentes (docs/specs/orcamento-plano-caixa-projecao.md). */
export function montarExplicacao(plano: PlanoDoMes, caixa: CaixaDoMes) {
  const pctCartao = Math.round(plano.noCartao.percentual)
  const linhasPlano: Linha[] = [
    { sinal: '+', titulo: 'Renda do mês', valor: plano.rendaDoMes, deOnde: 'Tudo o que entra no mês: o que já foi lançado e as receitas recorrentes que ainda vão cair' },
    { sinal: '−', titulo: 'Gasto até agora', valor: plano.gastoDoMes, deOnde: `Pela data da compra, no cartão ou à vista (${pctCartao}% no cartão)` },
    { sinal: '−', titulo: 'Falta nos orçamentos', valor: plano.aindaAGastarNosOrcamentos, deOnde: 'O que ainda cabe gastar nos orçamentos deste mês' },
    { sinal: '−', titulo: 'Contas sem orçamento a pagar', valor: plano.previstoForaDosOrcamentos, deOnde: 'Contas do mês ainda não pagas que não estão em nenhum orçamento' },
    { sinal: '=', titulo: plano.sobraPrevista >= 0 ? 'Sobra prevista' : 'Falta prevista', valor: plano.sobraPrevista, deOnde: 'O que sobra do mês pela data da compra' },
  ]

  const rendaBeneficio = r2(plano.rendaDoMes - caixa.rendaDoMes)
  const linhasCaixa: Linha[] = [
    { sinal: '+', titulo: 'Renda nas contas', valor: caixa.rendaDoMes, deOnde: rendaBeneficio > 0 ? `A renda do mês sem os ${formatMoney(rendaBeneficio)} que caíram em conta de benefício` : 'A renda do mês que cai nas contas do caixa' },
    { sinal: '−', titulo: 'Faturas que vencem', valor: caixa.faturasQueVencem, deOnde: 'As mesmas de "Veio de meses anteriores" (compras de meses anteriores e parcelas)' },
    { sinal: '−', titulo: 'À vista no mês', valor: caixa.aVistaDoMes, deOnde: caixa.aVistaDoMes > 0 ? 'Gasto à vista já feito no mês (débito, Pix, dinheiro)' : 'Ainda não houve gasto à vista neste mês' },
    { sinal: '−', titulo: 'Previsto à vista nos orçamentos', valor: caixa.previstoAVistaNosOrcamentos, deOnde: `A parte à vista dos ${formatMoney(plano.aindaAGastarNosOrcamentos)} que faltam nos orçamentos` },
    { sinal: '−', titulo: 'Contas sem orçamento a pagar', valor: caixa.previstoForaDosOrcamentos, deOnde: 'As mesmas do Plano: contas do mês ainda não pagas e sem orçamento' },
    { sinal: '−', titulo: 'Em aberto de meses anteriores', valor: caixa.emAberto, deOnde: 'Contas atrasadas e faturas vencidas que ainda não foram pagas' },
    { sinal: '=', titulo: 'Caixa previsto', valor: caixa.caixaPrevisto, deOnde: 'O que sobra nas contas no fim do mês' },
  ]

  // Por que os dois não batem: cada diferença só aparece quando tem valor
  const orcamentoFora = r2(plano.aindaAGastarNosOrcamentos - caixa.previstoAVistaNosOrcamentos)
  const cartaoDoMes = plano.noCartao.valor
  const gastoBeneficio = r2(plano.gastoDoMes - plano.noCartao.valor - caixa.aVistaDoMes)
  const soCaixa = r2(caixa.faturasQueVencem + caixa.emAberto)
  const diferencas = [
    rendaBeneficio > 0 && `Renda: ${formatMoney(rendaBeneficio)} entram no Plano, mas não no Caixa, porque caíram em conta de benefício (vale-alimentação), e não no banco.`,
    orcamentoFora > 0 && `Orçamentos: o Plano desconta os ${formatMoney(plano.aindaAGastarNosOrcamentos)} que faltam; o Caixa só a parte à vista (${formatMoney(caixa.previstoAVistaNosOrcamentos)}). O resto, ${formatMoney(orcamentoFora)}, vai para as faturas seguintes ou sai do benefício.`,
    cartaoDoMes > 0 && `Cartão: as compras no cartão deste mês (${formatMoney(cartaoDoMes)}) contam no Plano agora; no Caixa elas entram pela fatura, no mês em que ela vence.`,
    gastoBeneficio > 0.005 && `Fora do caixa: ${formatMoney(gastoBeneficio)} gastos com vale-alimentação (ou em conta que não entra no caixa) contam no Plano, mas não saem do caixa.`,
    soCaixa > 0 && `Faturas e em aberto (${formatMoney(soCaixa)}): só entram no Caixa, porque as compras contaram no Plano do mês em que foram feitas.`,
  ].filter((d): d is string => !!d)

  return { linhasPlano, linhasCaixa, diferencas }
}

function Tabela({ titulo, linhas }: { titulo: string; linhas: Linha[] }) {
  return (
    <section className="space-y-2">
      <h4 className="text-base font-semibold text-white">{titulo}</h4>
      <table className="w-full text-13">
        <tbody className="divide-y divide-border/40">
          {linhas.map((l) => (
            <tr key={l.titulo} className={l.sinal === '=' ? 'font-semibold' : ''}>
              <td className="py-1.5 pr-2 align-top text-white"><span className="mr-1 text-muted">{l.sinal}</span>{l.titulo}</td>
              <td className={`whitespace-nowrap py-1.5 pr-3 text-right align-top ${l.sinal === '=' ? (l.valor >= 0 ? 'text-income' : 'text-expense') : 'text-white'}`}>{formatMoney(l.valor)}</td>
              <td className="py-1.5 align-top text-2xs text-muted">{l.deOnde}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

export function ComoCalculamosModal({ plano, caixa, primeiro, onClose }: { plano: PlanoDoMes; caixa: CaixaDoMes; primeiro: 'plano' | 'caixa'; onClose: () => void }) {
  const { linhasPlano, linhasCaixa, diferencas } = montarExplicacao(plano, caixa)
  const p = <Tabela key="p" titulo="Plano do mês" linhas={linhasPlano} />
  const c = <Tabela key="c" titulo="Caixa do mês" linhas={linhasCaixa} />
  return (
    <Modal open onClose={onClose} title="Como esses números são calculados" size="lg" footer={<Button variant="light" onClick={onClose}>Fechar</Button>}>
      <div className="space-y-5">
        {primeiro === 'plano' ? [p, c] : [c, p]}
        {diferencas.length > 0 && (
          <section className="space-y-2">
            <h4 className="text-base font-semibold text-white">Por que os dois são diferentes</h4>
            <ol className="list-decimal space-y-1.5 pl-5 text-13 text-muted">{diferencas.map((d) => <li key={d}>{d}</li>)}</ol>
          </section>
        )}
      </div>
    </Modal>
  )
}
